import React from "react";
import type { ISharepointListLocation } from "../Services/LocationService";
import type { SiteItem } from "../../src/taskpane/redux/models/reduxModels";
import type { IClientGroupSearchSite } from "../../src/taskpane/components/helpers/sharepointSearch/clientGroupSearchScope";
import { fetchClientGroupLibraries } from "../Services/clientGroupLibrariesService";
import { groupSiteKey, hiddenLibraryTitlesForSite } from "../Services/clientGroupLibrarySources";

interface ILoadedLibraries {
    sitesKey: string;
    libraries: ISharepointListLocation[];
    failedSites: IClientGroupSearchSite[];
}

const NONE: ISharepointListLocation[] = [];
const NO_SITES: IClientGroupSearchSite[] = [];

/**
 * The document libraries of the client group's sites, for the All documents view (one Graph
 * batch, session-cached - see clientGroupLibrariesService), and the sites that could not be read.
 * A new `refreshToken` re-reads them while the libraries already known stay in use, so a refresh
 * does not drop the group's rows; what was read for another set of sites is never returned.
 */
export const useClientGroupLibraries = (
    sites: IClientGroupSearchSite[],
    siteSettings: SiteItem[] | undefined,
    refreshToken: number
): { libraries: ISharepointListLocation[]; failedSites: IClientGroupSearchSite[]; isLoading: boolean } => {
    const sitesKey: string = (sites ?? []).map(site => groupSiteKey(site) ?? "").join("|");
    const [loaded, setLoaded] = React.useState<ILoadedLibraries>({ sitesKey: "", libraries: NONE, failedSites: NO_SITES });
    const [pendingKey, setPendingKey] = React.useState<string>("");
    const sitesRef = React.useRef<IClientGroupSearchSite[]>(sites);
    sitesRef.current = sites;
    // The refresh token of the last read per set of sites: a changed token forces a re-read.
    const lastReadRef = React.useRef<{ sitesKey: string; refreshToken: number } | null>(null);

    React.useEffect(() => {
        if (!sitesKey) {
            lastReadRef.current = null;
            return undefined;
        }
        const forceRefresh: boolean = lastReadRef.current?.sitesKey === sitesKey && lastReadRef.current.refreshToken !== refreshToken;
        lastReadRef.current = { sitesKey, refreshToken };
        const requestKey = `${sitesKey}#${refreshToken}`;
        let isCancelled = false;
        setPendingKey(requestKey);
        fetchClientGroupLibraries(sitesRef.current, site => hiddenLibraryTitlesForSite(siteSettings, site?.displayName), forceRefresh)
            .then(({ libraries, failedSites }) => {
                if (!isCancelled) {
                    setLoaded({ sitesKey, libraries, failedSites: failedSites.length > 0 ? failedSites : NO_SITES });
                    setPendingKey(current => current === requestKey ? "" : current);
                }
            });
        return () => {
            isCancelled = true;
        };
    }, [sitesKey, refreshToken, siteSettings]);

    if (!sitesKey) {
        return { libraries: NONE, failedSites: NO_SITES, isLoading: false };
    }
    const isCurrent: boolean = loaded.sitesKey === sitesKey;
    return {
        libraries: isCurrent ? loaded.libraries : NONE,
        failedSites: isCurrent ? loaded.failedSites : NO_SITES,
        isLoading: !isCurrent || pendingKey === `${sitesKey}#${refreshToken}`,
    };
};
